// Ported from desktop/src/renderer/src/features/channels/ChannelList.tsx,
// adapted for web (types from @shared/api instead of @shared/telegram). Keeps
// the identical tg-chat-row styling + trading-kind indicator stripe.
import type { TelegramChannelSummary } from "@shared/api/schemas/telegram";
import type { TradingChannelKind } from "@shared/api/schemas/trading-channels";
import { getAvatarLabel, getAvatarToneClass } from "../../lib/avatar";

interface ChannelListProps {
	channels: TelegramChannelSummary[];
	emptyDescription?: string;
	emptyTitle?: string;
	isLoading?: boolean;
	onSelect: (channelId: number) => void;
	selectedChatId: number | null;
	tradingKindsByChatId: Map<number, TradingChannelKind>;
}

const getTradingChannelClassName = (
	kind: TradingChannelKind | undefined,
	isSelected: boolean,
) => {
	if (isSelected) return "tg-chat-row-selected";
	if (kind === "text") return "tg-chat-row-text";
	if (kind === "image") return "tg-chat-row-image";
	return "tg-chat-row";
};

export const ChannelList = ({
	channels,
	emptyDescription,
	emptyTitle,
	isLoading = false,
	onSelect,
	selectedChatId,
	tradingKindsByChatId,
}: ChannelListProps) => {
	if (channels.length === 0) {
		if (isLoading) {
			return (
				<div className="flex h-full min-h-[240px] flex-col items-center justify-center px-4 py-6 text-center tg-text-muted">
					<div
						className="tg-spinner h-8 w-8 animate-spin rounded-full border-2"
						aria-hidden="true"
					/>
					<h3 className="mt-4 text-lg font-semibold tg-text-primary">
						Loading chats
					</h3>
					<p className="mt-2 leading-7">
						Loading the Telegram chat list for this profile.
					</p>
				</div>
			);
		}

		return (
			<div className="px-4 py-6 text-center tg-text-muted">
				<h3 className="mb-2 text-lg font-semibold tg-text-primary">
					{emptyTitle ?? "No chats found"}
				</h3>
				<p className="leading-7">
					{emptyDescription ??
						"This account may not have any Telegram chats yet, or the current filter hid them."}
				</p>
			</div>
		);
	}

	return (
		<div className="tg-scrollbar grid h-full min-h-0 min-w-0 grid-cols-1 auto-rows-min gap-[2px] overflow-y-auto overflow-x-hidden pr-1">
			{channels.map((channel) => {
				const tradingKind = tradingKindsByChatId.get(channel.id);
				const isSelected = selectedChatId === channel.id;
				const indicatorClassName =
					tradingKind === "text"
						? "tg-trading-indicator-text"
						: tradingKind === "image"
							? "tg-trading-indicator-image"
							: "bg-transparent";

				return (
					<button
						key={channel.id}
						className={`group relative grid w-full min-w-0 overflow-hidden min-h-[60px] cursor-pointer grid-cols-[auto_minmax(0,1fr)] gap-3 rounded-xl border border-transparent px-3 py-2.5 text-left transition ${getTradingChannelClassName(
							tradingKind,
							isSelected,
						)}`}
						type="button"
						onClick={() => onSelect(channel.id)}
					>
						<span
							className={`absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-r-full ${indicatorClassName}`}
							aria-hidden="true"
						/>
						<div
							className={`tg-avatar-text inline-flex h-10 w-10 items-center justify-center rounded-full text-[0.82rem] font-bold ${getAvatarToneClass(channel.title)}`}
						>
							{getAvatarLabel(channel.title)}
						</div>

						<div className="min-w-0 w-full self-center">
							<h3
								className={`block min-w-0 w-full truncate text-[0.95rem] font-semibold ${
									isSelected ? "tg-text-on-active" : "tg-text-primary"
								}`}
							>
								{channel.title}
							</h3>
							<p
								className={`mt-0.5 block min-w-0 w-full truncate text-[0.86rem] leading-5 ${
									isSelected ? "tg-text-on-active-muted" : "tg-text-muted"
								}`}
							>
								{channel.lastMessagePreview}
							</p>
						</div>
					</button>
				);
			})}
		</div>
	);
};
